import axios from 'axios'

/** 内部券类型 → 用户可见名称 */
export const COUPON_TYPE_LABELS = {
  normal: '双享礼券',
  partner: '臻享礼券',
  cash: '专享礼券'
}

/** 列表筛选（臻享礼券在上方独立展示，不在此列表重复） */
export const LIST_COUPON_TYPE_LABELS = {
  normal: '双享礼券',
  cash: '专享礼券'
}

/** 券类型简要说明 */
export const COUPON_TYPE_HINTS = {
  normal: '系统生成 RC 券号，创建后不可修改；绑定后双方获礼遇，每张仅用一次',
  partner: '每人一个，可自定义券号；被使用后不可修改',
  cash: '管理员发放，创建后不可修改；每张仅用一次'
}

/** 双享礼券创建说明 */
export const NORMAL_COUPON_CREATE_HINT =
  '填写名称后创建，系统自动生成 RC 券号。创建后不可修改，被绑定后即失效。'

/** 臻享礼券规则说明 */
export const PARTNER_COUPON_HINT =
  '臻享资格由管理员开通。每位臻享伙伴仅有一个臻享礼券，可自定义券号（4～20 位，字母开头，不能以 RC 开头）。被使用后不可修改。'

/** 臻享分润范围说明 */
export const PARTNER_COMMISSION_SCOPE_HINT =
  '专属分润目前仅计入客户在平台购买测评/课程等内容时的订单金额（按配置比例），充值等不纳入分润。申诉期内显示为预估，申诉期结束后 T+N 入账。'

export function buildBindBonusHint(couponType, settings) {
  const ref = Number(settings?.refereeBonusCents || 0) / 100
  const reg = Number(settings?.referrerBonusCents || 0) / 100
  const partnerExtra = Number(settings?.partnerBindBonusCents || 0) / 100
  if (couponType === 'cash') {
    if (ref > 0) return `专享礼券：绑定后您约可得 ¥${ref.toFixed(2)} 礼遇（推荐人不发双享赠金）`
    return '专享礼券：仅被推荐人获得礼遇，推荐人不发双享赠金'
  }
  if (couponType === 'partner') {
    let msg = '臻享礼券：绑定后建立终身分润关系'
    if (ref > 0 || reg > 0) {
      msg += `；双享礼遇约推荐人 ¥${reg.toFixed(2)}、您 ¥${ref.toFixed(2)}`
    }
    if (partnerExtra > 0) msg += `；另含见面礼约 ¥${partnerExtra.toFixed(2)}`
    return msg
  }
  if (ref <= 0 && reg <= 0) return ''
  return `双享礼券绑定成功后，推荐人约 ¥${reg.toFixed(2)}、被绑人约 ¥${ref.toFixed(2)} 礼遇。`
}

export function couponTypeLabel(type) {
  return COUPON_TYPE_LABELS[type] || type || '—'
}

export function couponTypeHint(type) {
  return COUPON_TYPE_HINTS[type] || ''
}

export async function checkReferralCoupon(couponNo) {
  const no = (couponNo || '').trim()
  if (!no) return { valid: false, message: '' }
  const res = await axios.get('/api/v1/referral/coupon/check', { params: { couponNo: no } })
  return res.data?.data || { valid: false, message: res.data?.message || '礼券无效' }
}

export async function bindReferralCoupon(couponNo) {
  const no = (couponNo || '').trim()
  if (!no) throw new Error('请输入礼券号')
  const res = await axios.post('/api/v1/referral/bind', { couponNo: no })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '绑定失败')
  }
  return res.data
}

export async function fetchReferralBindStatus() {
  const res = await axios.get('/api/v1/referral/bind/status')
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || {}
}

let cachedReferralSettings = null

export async function fetchReferralSettings(force = false) {
  if (!force && cachedReferralSettings) {
    return cachedReferralSettings
  }
  const res = await axios.get('/api/v1/referral/settings')
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载配置失败')
  }
  cachedReferralSettings = res.data.data || {}
  return cachedReferralSettings
}

export async function fetchMyReferralBindings(page = 1, pageSize = 20, couponType = '') {
  const res = await axios.post('/api/v1/referral/bindings/my-list', {
    page,
    pageSize,
    couponType: couponType || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || { bindings: [], total: 0 }
}

export async function fetchMyPartnerCustomers(page = 1, pageSize = 20) {
  const res = await axios.post('/api/v1/referral/commission/customers', { page, pageSize })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || { items: [], total: 0 }
}

export async function adminUnblockReferralBonus({ userId, username } = {}) {
  const res = await axios.post('/api/v1/admin/referral/user/unblock-bonus', {
    userId: userId || undefined,
    username: username || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '操作失败')
  }
  return res.data
}

export async function adminClawbackReferralBonus(bindingId) {
  const res = await axios.post('/api/v1/admin/referral/bindings/clawback-bonus', { bindingId })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '冲正失败')
  }
  return res.data.data || {}
}

export async function adminCancelPartnerCommission({ ledgerId, orderNo } = {}) {
  const res = await axios.post('/api/v1/admin/referral/commission/cancel', {
    ledgerId: ledgerId || undefined,
    orderNo: orderNo || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '取消失败')
  }
  return res.data.data || {}
}

export async function adminUpdateReferralSettings(payload) {
  const res = await axios.post('/api/v1/admin/referral/settings/update', payload)
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '保存失败')
  }
  cachedReferralSettings = res.data.data || null
  return res.data.data
}

export async function fetchMyPartnerCommission(page = 1, pageSize = 20, status = '') {
  const res = await axios.post('/api/v1/referral/commission/my-list', {
    page,
    pageSize,
    status: status || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || { items: [], total: 0 }
}

export async function fetchPartnerReferralCoupon() {
  const res = await axios.get('/api/v1/referral/coupon/partner/mine')
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || null
}

export async function savePartnerReferralCoupon({ couponNo, displayName }) {
  const res = await axios.post('/api/v1/referral/coupon/partner/set', { couponNo, displayName })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '保存失败')
  }
  return res.data.data
}

export function buildCouponBindUrl(couponNo) {
  const no = (couponNo || '').trim()
  if (!no) return ''
  return `${window.location.origin}/referral/bind-coupon?coupon=${encodeURIComponent(no)}`
}

export function buildRegisterWithCouponUrl(couponNo) {
  const no = (couponNo || '').trim()
  if (!no) return ''
  return `${window.location.origin}/register?coupon=${encodeURIComponent(no)}`
}

export function commissionStatusLabel(status) {
  const map = { settled: '已到账', pending: '待到账', estimated: '预估', cancelled: '已取消' }
  return map[status] || status || '—'
}

export function centsToYuan(cents) {
  return (Number(cents || 0) / 100).toFixed(2)
}

export async function fetchAdminReferralUserOverview({ userId, username } = {}) {
  const res = await axios.post('/api/v1/admin/referral/user/overview', {
    userId: userId || undefined,
    username: username || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '查询失败')
  }
  return res.data.data
}

export async function fetchAdminReferralBindAttempts(page = 1, pageSize = 20, filters = {}) {
  const res = await axios.post('/api/v1/admin/referral/bind-attempts/list', {
    page,
    pageSize,
    userId: filters.userId || undefined,
    clientIp: filters.clientIp || undefined,
    couponNo: filters.couponNo || undefined,
    success: filters.success || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || { items: [], total: 0 }
}

export async function fetchAdminReferralActions(page = 1, pageSize = 20, action = '') {
  const res = await axios.post('/api/v1/admin/referral/actions/list', {
    page,
    pageSize,
    action: action || undefined
  })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '加载失败')
  }
  return res.data.data || { items: [], total: 0 }
}

export async function adminRetryReferralBonus(bindingId) {
  const res = await axios.post('/api/v1/admin/referral/bindings/retry-bonus', { bindingId })
  if (res.data?.code !== 200) {
    throw new Error(res.data?.message || '补发失败')
  }
  return res.data
}
